import React from 'react';

import UserInListModel from '@Api/models/UserInListModel';
import UserModel from '@Api/models/UserModel';

import { LinkColor, LinkSize, LinkWeight } from '@Components/atoms/Link/Link';
import Row from '@Components/atoms/Row';
import { RowGap } from '@Components/atoms/Row/Row';
import Skeleton from '@Components/atoms/Skeleton';
import Text from '@Components/atoms/Text';
import { Tooltip, TooltipContent } from '@Components/atoms/Tooltip/Tooltip';
import UserImage from '@Components/atoms/UserImage';
import { UserImageSize } from '@Components/atoms/UserImage/UserImage';
import { PropsWithClassName } from '@Components/helper';
import { getDynamicSubRoute } from '@Components/helper/url';
import { getStringWithLength } from '@Components/helper/utils';
import UserInfo from '@Components/molecules/UserInfo/UserInfo';
import { Routes } from '@Components/Routes';

import useRouter from '@Helpers/i18n/useRouter';
import { TextSize } from '@Helpers/types/text';

import { Root, StyledTooltipTrigger, UsernameText } from './UserLink.styles';

interface Props extends PropsWithClassName {
  user: UserModel | UserInListModel;
  textSize?: LinkSize;
}

const UserLink = (props: Props): React.ReactElement => {
  const { user, textSize = LinkSize.Xs } = props;
  const router = useRouter();

  return (
    <Root className={props.className} data-testid={props.dataTestId ?? 'user-link-root'}>
      <Tooltip>
        <StyledTooltipTrigger>
          <UserImage
            userImageSrcModel={user.gravatar}
            userImageSize={UserImageSize.Small}
            width={30}
            height={30}
            onClick={() => router.push(getDynamicSubRoute(Routes.User, user.username))}
          />
          <UsernameText
            href={getDynamicSubRoute(Routes.User, user.username)}
            size={textSize}
            weight={LinkWeight.Light}
            color={LinkColor.Primary}
            hideUnderline
          >
            {user.username}
          </UsernameText>
        </StyledTooltipTrigger>
        <TooltipContent>
          <UserInfo user={user} />
        </TooltipContent>
      </Tooltip>
    </Root>
  );
};

const UserLinkSkeleton = (props: PropsWithClassName): React.ReactElement => {
  return (
    <div className={props.className} data-testid={props.dataTestId ?? 'user-link-skeleton-root'}>
      <Row noGutters rowGap={RowGap.Xs} xsMiddle>
        <Skeleton width="30px" height="30px" round />
        <Skeleton>
          <Text size={TextSize.Small}>{getStringWithLength(25)}</Text>
        </Skeleton>
      </Row>
    </div>
  );
};

UserLink.Skeleton = UserLinkSkeleton;

export default UserLink;
